/**
 * @fileoverview Bond-angle and dihedral-angle geometry plus their analytical
 * per-atom Cartesian gradients.
 *
 * These are the geometric primitives the bonded force kernels
 * (`forces/bonded.js`, owned by another agent) build on:
 *   - `bondAngle` / `angleGrad` for the harmonic-angle term.
 *   - `dihedralAngle` / `dihedralGrad` for the periodic-torsion term.
 *
 * The gradient routines return the angle (so a caller that needs both the
 * value and the derivative pays for the trig once) and write `partial(angle)/
 * partial(x)` into a caller-supplied output buffer. The force kernel then
 * scales those derivatives by `dE/d(angle)` and accumulates into the global
 * gradient — no allocation happens here on the hot path.
 *
 * Conventions (engine contract):
 *   - `coords` is a flat `Float64Array` of length `3*N`; atom `a` occupies
 *     `[3a, 3a+1, 3a+2]`. Indices `ia, ib, ...` are atom indices (not
 *     component offsets); we multiply by 3 internally.
 *   - Angles are in radians. `dihedralAngle` returns phi in (-pi, pi].
 *
 * Dihedral gradient form: the standard Blondel–Karplus / OpenMM
 * cross-product expression (same as OpenMM's
 * `ReferenceBondForce`/`ReferenceProperDihedralBond`):
 *
 *     b1 = rib - ria,  b2 = ric - rib,  b3 = rid - ric
 *     n1 = b1 x b2,    n2 = b2 x b3
 *     phi = atan2( |b2| * b1 . n2 , n1 . n2 )
 *     dphi/dr_a = -|b2| / |n1|^2 * n1
 *     dphi/dr_d =  |b2| / |n2|^2 * n2
 *     with p = (b1.b2)/|b2|^2,  q = (b3.b2)/|b2|^2 :
 *     dphi/dr_b = (-1 - p) * dphi/dr_a + q * dphi/dr_d
 *     dphi/dr_c =  p       * dphi/dr_a + (-1 - q) * dphi/dr_d
 *
 * The b/c terms are fixed by translational invariance (the four per-atom
 * gradients sum to zero); the grouping above is validated to <1e-5 relative
 * error against central differences in angle.test.js.
 */

// Allocation note: these routines keep every cross-product intermediate in
// plain f64 locals (component-wise), so they allocate nothing per call and need
// no module-level scratch here. The caller supplies the output buffer (out9 /
// out12); the reusable SCRATCH9/SCRATCH12 grad buffers live in bonded.js, which
// owns the force kernels that drive these primitives.

/**
 * Bond angle at vertex `ib`, formed by atoms ia–ib–ic. Returns theta in
 * radians, in [0, pi].
 *
 * @param {Float64Array} coords   Flat length-3N coordinate buffer (nm).
 * @param {number} ia
 * @param {number} ib             Vertex atom.
 * @param {number} ic
 * @returns {number}              theta in rad.
 */
export function bondAngle(coords, ia, ib, ic) {
    const a3 = 3 * ia;
    const b3 = 3 * ib;
    const c3 = 3 * ic;

    // Vectors from the vertex to each end.
    const ux = coords[a3] - coords[b3];
    const uy = coords[a3 + 1] - coords[b3 + 1];
    const uz = coords[a3 + 2] - coords[b3 + 2];
    const vx = coords[c3] - coords[b3];
    const vy = coords[c3 + 1] - coords[b3 + 1];
    const vz = coords[c3 + 2] - coords[b3 + 2];

    const uu = ux * ux + uy * uy + uz * uz;
    const vv = vx * vx + vy * vy + vz * vz;
    const uv = ux * vx + uy * vy + uz * vz;

    const denom = Math.sqrt(uu * vv);
    if (denom === 0) return 0;
    let c = uv / denom;
    if (c > 1) c = 1;
    else if (c < -1) c = -1;
    return Math.acos(c);
}

/**
 * Bond angle and its Cartesian gradient.
 *
 * Writes partial(theta)/partial(x) into `out9` as
 * `[da_x, da_y, da_z, db_x, db_y, db_z, dc_x, dc_y, dc_z]` (the vertex `ib`
 * derivative occupies slots 3..5). Returns theta in rad.
 *
 * Standard form: with u = ra - rb, v = rc - rb,
 *   dtheta/dra = (cos(theta) * u/|u| - v/|v|) / (|u| sin(theta))
 *   dtheta/drc = (cos(theta) * v/|v| - u/|u|) / (|v| sin(theta))
 *   dtheta/drb = -(dtheta/dra + dtheta/drc)
 * Degenerate (near-linear / zero-length) cases write a zero gradient.
 *
 * @param {Float64Array} coords
 * @param {number} ia
 * @param {number} ib             Vertex atom.
 * @param {number} ic
 * @param {Float64Array} out9     Length-9 output buffer for the gradient.
 * @returns {number}             theta in rad.
 */
export function angleGrad(coords, ia, ib, ic, out9) {
    const a3 = 3 * ia;
    const b3 = 3 * ib;
    const c3 = 3 * ic;

    const ux = coords[a3] - coords[b3];
    const uy = coords[a3 + 1] - coords[b3 + 1];
    const uz = coords[a3 + 2] - coords[b3 + 2];
    const vx = coords[c3] - coords[b3];
    const vy = coords[c3 + 1] - coords[b3 + 1];
    const vz = coords[c3 + 2] - coords[b3 + 2];

    const lu = Math.sqrt(ux * ux + uy * uy + uz * uz);
    const lv = Math.sqrt(vx * vx + vy * vy + vz * vz);

    if (lu === 0 || lv === 0) {
        out9.fill(0);
        return 0;
    }

    // Unit vectors.
    const uhx = ux / lu; const uhy = uy / lu; const uhz = uz / lu;
    const vhx = vx / lv; const vhy = vy / lv; const vhz = vz / lv;

    let cosT = uhx * vhx + uhy * vhy + uhz * vhz;
    if (cosT > 1) cosT = 1;
    else if (cosT < -1) cosT = -1;
    const theta = Math.acos(cosT);

    const sinT = Math.sqrt(1 - cosT * cosT);
    if (sinT < 1e-8) {
        // Near-linear (0 or pi): gradient is ill-defined; return zero so the
        // force kernel applies no force from this near-degenerate angle.
        out9.fill(0);
        return theta;
    }

    // dtheta/dra = (cosT * uhat - vhat) / (|u| sinT)
    const invUsin = 1 / (lu * sinT);
    const invVsin = 1 / (lv * sinT);

    const dax = (cosT * uhx - vhx) * invUsin;
    const day = (cosT * uhy - vhy) * invUsin;
    const daz = (cosT * uhz - vhz) * invUsin;

    const dcx = (cosT * vhx - uhx) * invVsin;
    const dcy = (cosT * vhy - uhy) * invVsin;
    const dcz = (cosT * vhz - uhz) * invVsin;

    out9[0] = dax; out9[1] = day; out9[2] = daz;
    out9[3] = -(dax + dcx); out9[4] = -(day + dcy); out9[5] = -(daz + dcz);
    out9[6] = dcx; out9[7] = dcy; out9[8] = dcz;

    return theta;
}

/**
 * Dihedral (torsion) angle for the atom sequence ia–ib–ic–id, measured about
 * the ib–ic bond. Returns phi in radians in (-pi, pi].
 *
 * Sign convention matches OpenMM / IUPAC: looking down b->c, phi is the
 * angle from the ib->ia projection to the ic->id projection, positive for a
 * clockwise (right-handed) rotation.
 *
 * @param {Float64Array} coords
 * @param {number} ia
 * @param {number} ib
 * @param {number} ic
 * @param {number} id
 * @returns {number}             phi in rad, (-pi, pi].
 */
export function dihedralAngle(coords, ia, ib, ic, id) {
    const a3 = 3 * ia;
    const b3 = 3 * ib;
    const c3 = 3 * ic;
    const d3 = 3 * id;

    // Bond vectors.
    const b1x = coords[b3] - coords[a3];
    const b1y = coords[b3 + 1] - coords[a3 + 1];
    const b1z = coords[b3 + 2] - coords[a3 + 2];
    const b2x = coords[c3] - coords[b3];
    const b2y = coords[c3 + 1] - coords[b3 + 1];
    const b2z = coords[c3 + 2] - coords[b3 + 2];
    const b3x = coords[d3] - coords[c3];
    const b3y = coords[d3 + 1] - coords[c3 + 1];
    const b3z = coords[d3 + 2] - coords[c3 + 2];

    // n1 = b1 x b2, n2 = b2 x b3.
    const n1x = b1y * b2z - b1z * b2y;
    const n1y = b1z * b2x - b1x * b2z;
    const n1z = b1x * b2y - b1y * b2x;
    const n2x = b2y * b3z - b2z * b3y;
    const n2y = b2z * b3x - b2x * b3z;
    const n2z = b2x * b3y - b2y * b3x;

    const lb2 = Math.sqrt(b2x * b2x + b2y * b2y + b2z * b2z);

    // phi = atan2( |b2| * (b1 . n2), n1 . n2 )
    const n1dotn2 = n1x * n2x + n1y * n2y + n1z * n2z;
    const b1dotn2 = b1x * n2x + b1y * n2y + b1z * n2z;
    return Math.atan2(lb2 * b1dotn2, n1dotn2);
}

/**
 * Dihedral angle and its Cartesian gradient (Blondel–Karplus / OpenMM form).
 *
 * Writes partial(phi)/partial(x) into `out12` as
 * `[da(3), db(3), dc(3), dd(3)]`. Returns phi in rad in (-pi, pi].
 *
 * Near-degenerate cases (|n1| or |n2| ~ 0, i.e. three collinear atoms making a
 * cross product vanish) write a zero gradient and return phi computed from
 * atan2 (which is still well-defined up to the usual branch).
 *
 * @param {Float64Array} coords
 * @param {number} ia
 * @param {number} ib
 * @param {number} ic
 * @param {number} id
 * @param {Float64Array} out12    Length-12 output buffer for the gradient.
 * @returns {number}             phi in rad, (-pi, pi].
 */
export function dihedralGrad(coords, ia, ib, ic, id, out12) {
    const a3 = 3 * ia;
    const b3 = 3 * ib;
    const c3 = 3 * ic;
    const d3 = 3 * id;

    const b1x = coords[b3] - coords[a3];
    const b1y = coords[b3 + 1] - coords[a3 + 1];
    const b1z = coords[b3 + 2] - coords[a3 + 2];
    const b2x = coords[c3] - coords[b3];
    const b2y = coords[c3 + 1] - coords[b3 + 1];
    const b2z = coords[c3 + 2] - coords[b3 + 2];
    const b3x = coords[d3] - coords[c3];
    const b3y = coords[d3 + 1] - coords[c3 + 1];
    const b3z = coords[d3 + 2] - coords[c3 + 2];

    // n1 = b1 x b2, n2 = b2 x b3.
    const n1x = b1y * b2z - b1z * b2y;
    const n1y = b1z * b2x - b1x * b2z;
    const n1z = b1x * b2y - b1y * b2x;
    const n2x = b2y * b3z - b2z * b3y;
    const n2y = b2z * b3x - b2x * b3z;
    const n2z = b2x * b3y - b2y * b3x;

    const n1sq = n1x * n1x + n1y * n1y + n1z * n1z;
    const n2sq = n2x * n2x + n2y * n2y + n2z * n2z;
    const b2sq = b2x * b2x + b2y * b2y + b2z * b2z;
    const lb2 = Math.sqrt(b2sq);

    const n1dotn2 = n1x * n2x + n1y * n2y + n1z * n2z;
    const b1dotn2 = b1x * n2x + b1y * n2y + b1z * n2z;
    const phi = Math.atan2(lb2 * b1dotn2, n1dotn2);

    if (n1sq < 1e-18 || n2sq < 1e-18 || b2sq < 1e-18) {
        // Collinear triplet -> torsion plane undefined. No gradient.
        out12.fill(0);
        return phi;
    }

    // dphi/dr_a = -|b2|/|n1|^2 * n1
    const fa = -lb2 / n1sq;
    const dax = fa * n1x;
    const day = fa * n1y;
    const daz = fa * n1z;

    // dphi/dr_d =  |b2|/|n2|^2 * n2
    const fd = lb2 / n2sq;
    const ddx = fd * n2x;
    const ddy = fd * n2y;
    const ddz = fd * n2z;

    // Projection factors.
    const b1dotb2 = b1x * b2x + b1y * b2y + b1z * b2z;
    const b3dotb2 = b3x * b2x + b3y * b2y + b3z * b2z;
    const p = b1dotb2 / b2sq;
    const q = b3dotb2 / b2sq;

    // dphi/dr_b = (-1 - p) * dphi/dr_a + q * dphi/dr_d
    // dphi/dr_c =  p       * dphi/dr_a + (-1 - q) * dphi/dr_d
    // (validated by finite difference; satisfies da+db+dc+dd = 0.)
    const dbx = (-1 - p) * dax + q * ddx;
    const dby = (-1 - p) * day + q * ddy;
    const dbz = (-1 - p) * daz + q * ddz;

    const dcx = p * dax + (-1 - q) * ddx;
    const dcy = p * day + (-1 - q) * ddy;
    const dcz = p * daz + (-1 - q) * ddz;

    out12[0] = dax; out12[1] = day; out12[2] = daz;
    out12[3] = dbx; out12[4] = dby; out12[5] = dbz;
    out12[6] = dcx; out12[7] = dcy; out12[8] = dcz;
    out12[9] = ddx; out12[10] = ddy; out12[11] = ddz;

    return phi;
}
